/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-len */
import { XumoButton as Button, StackedContent } from 'ui';
import Image from 'next/image';
import { useScroll } from 'framer-motion';
import { useState, useRef, useEffect, useCallback } from 'react';

export interface CTATileCarouselProps {
  bgImage?: string;
  center?: boolean;
  header?: any;
  tiles: any[];
  bgColor?: string;
  cardBgColor?: string;
  className?: string;
  leadingGutterClass?: string;
  trailingGutterClass?: string;
}

export const CTATileCarousel = ({
  bgImage,
  center = true,
  header,
  tiles,
  bgColor = 'white',
  cardBgColor = 'white',
  className,
  leadingGutterClass = '',
  trailingGutterClass = '',
}: CTATileCarouselProps) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const scrollbarTrackRef = useRef<HTMLButtonElement | null>(null);

  // --- Scroll to specific card when indicator clicked
  const scrollToIndex = useCallback((index: number) => {
    const el = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!el || !card) return;

    setIsUserScrolling(true);

    const cardLeft = card.offsetLeft;
    const cardWidth = card.offsetWidth;
    const containerWidth = el.clientWidth;
    const targetScroll = cardLeft - (containerWidth - cardWidth) / 2;

    el.scrollTo({
      left: Math.max(0, targetScroll),
      behavior: 'smooth',
    });

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setIsUserScrolling(false), 600);
  }, []);

  const handleSelect = (index: number) => {
    scrollToIndex(index);
  };

  // --- Track scroll position and update indicator
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let rafId = 0;

    const updateCurrentIndex = () => {
      const el = scrollerRef.current;
      if (!el) return;

      const scrollLeft = el.scrollLeft;
      const containerWidth = el.clientWidth;
      const scrollCenter = scrollLeft + containerWidth / 2;

      let closest = 0;
      let min = Infinity;

      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const d = Math.abs(scrollCenter - cardCenter);
        if (d < min) {
          min = d;
          closest = i;
        }
      });

      if (closest !== currentIndex) setCurrentIndex(closest);
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateCurrentIndex);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    updateCurrentIndex();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [currentIndex, isUserScrolling]);

  // FOR DESKTOP SCROLLBAR FUNCTIONALITY
  const { scrollXProgress } = useScroll({
    container: scrollerRef,
  });

  // Update progress state when scrollXProgress changes
  useEffect(() => {
    return scrollXProgress.on('change', latest => {
      setScrollProgress(latest);
    });
  }, [scrollXProgress]);

  const handleScrollbarInteraction = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const track = scrollbarTrackRef.current;
    const scroller = scrollerRef.current;
    if (!track || !scroller) return;

    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const trackWidth = rect.width;
    const indicatorWidth = 150;

    const maxTravel = trackWidth - indicatorWidth;
    const targetProgress = Math.max(0, Math.min(1, clickX / maxTravel));

    const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    const targetScroll = targetProgress * maxScroll;

    scroller.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsDragging(true);
      handleScrollbarInteraction(e);
    },
    [handleScrollbarInteraction],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const track = scrollbarTrackRef.current;
      const scroller = scrollerRef.current;
      if (!track || !scroller) return;

      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const trackWidth = rect.width;
      const indicatorWidth = 150;

      const maxTravel = trackWidth - indicatorWidth;
      const targetProgress = Math.max(0, Math.min(1, clickX / maxTravel));

      const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
      const targetScroll = targetProgress * maxScroll;

      scroller.scrollTo({
        left: targetScroll,
        behavior: 'auto',
      });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <section className={`${bgImage ? 'relative flex w-full overflow-clip' : 'h-fit'} bg-${bgColor}`} data-testid="text-section">
      {/* Image */}
      {bgImage && (
        <div className="absolute inset-0 z-0">
          <div className="relative h-full w-full">
            <Image src={bgImage} alt="" fill className="object-cover" />
            <div
              className="absolute inset-0 z-10"
              style={{
                background: 'linear-gradient(180deg, #140D13 0%, rgba(20, 13, 19, 0.80) 29%, rgba(20, 13, 19, 0.00) 100%)',
              }}
            />
          </div>
        </div>
      )}

      <div className="relative z-20 w-full">
        {/* Header */}
        {header && (
          <div className="wrapper pb-0 pt-16 lg:pt-32">
            <StackedContent center={center} mobileCenter={center} content={header} className={className} />
          </div>
        )}

        {/* Scrollable cards */}
        <div
          ref={scrollerRef}
          className="hide-scroll flex w-full snap-x snap-mandatory items-start gap-10 overflow-x-auto pr-12 md:snap-none md:pr-0 xl:gap-24"
        >
          <div className={`shrink-0 ${leadingGutterClass}`} aria-hidden />
          {tiles.map((tile: any, index: number) => (
            <div
              key={index}
              ref={el => {
                cardRefs.current[index] = el;
              }}
              className="shrink-0 snap-center"
              style={{
                scrollSnapAlign: 'center',
                scrollSnapStop: 'always',
              }}
            >
              <div
                className={`min-h-[460px] w-[calc(100vw-3rem)] max-w-[600px] rounded-2xl border-[0.5px] border-white md:min-h-[500px] md:max-w-none bg-${cardBgColor} } items-start px-6 py-8 md:min-h-[469px] md:w-[600px] md:px-8 md:py-12 lg:md:min-h-[600px] 2xl:min-h-[743px] 2xl:w-[880px]`}
              >
                <StackedContent center={center} space content={tile} className={className} noMargin={true} />
              </div>
            </div>
          ))}
          <div className={`shrink-0 ${trailingGutterClass}`} aria-hidden />
        </div>

        {/* Mobile scroll indicators */}
        <div className="z-20 mb-16 mt-8 flex w-full justify-center gap-2 md:hidden">
          {tiles.map((_, index) => (
            <button
              key={index}
              onClick={() => handleSelect(index)}
              className={`h-3 w-3 rounded-full transition-all duration-300 ${currentIndex === index ? 'w-8 bg-white' : 'bg-white/30'}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Desktop scrollbar */}
        <div className="mt-20 hidden w-full justify-center pb-16 md:flex">
          <button
            ref={scrollbarTrackRef}
            onMouseDown={handleMouseDown}
            className="relative z-20 flex h-[10px] w-[750px] cursor-pointer items-center overflow-hidden rounded-full border-0 bg-xumoDarkPlum p-0"
            aria-label="Scroll carousel"
          >
            <div
              className="pointer-events-none h-[10px] w-[150px] rounded-full bg-[#DBE4E9]"
              style={{
                transform: `translateX(${scrollProgress * 600}px)`,
                transition: isDragging ? 'none' : 'transform 200ms ease-out',
              }}
            ></div>
          </button>
        </div>
      </div>
    </section>
  );
};
